import { FC } from 'react';
import Box from '@mui/material/Box';
import DropdownArrowDownIcon from '../../../assets/svg-fc/DropdownArrowDownIcon';
import PhCaretRightSvg from '../../../assets/svg-fc/PhCaretRightSvg';
import { TABLE_ROWS_PER_PAGE_OPTIONS, UI_TOKENS } from '../../../constants/designTokens';

interface iOnboardingTableFooterProps {
  /** 1-based, as Baqaal's `meta.currentPage`. */
  page: number;
  totalPages: number;
  rowsPerPage: number;
  total: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
}

const pageButtonSx = (disabled: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  border: 'none',
  borderRadius: '50%',
  bgcolor: 'transparent',
  color: disabled ? UI_TOKENS.grey400 : UI_TOKENS.textStrong,
  cursor: disabled ? 'default' : 'pointer',
  '&:hover': disabled ? {} : { bgcolor: UI_TOKENS.surfaceHover },
});

/** Paging is server-side — the range and page count come from the queue's `meta`. */
const TableFooter: FC<iOnboardingTableFooterProps> = ({
  page,
  totalPages,
  rowsPerPage,
  total,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = TABLE_ROWS_PER_PAGE_OPTIONS,
}) => {
  const rangeStart = total === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const rangeEnd = Math.min(total, page * rowsPerPage);
  const rangeLabel = total === 0 ? '0 of 0' : `${rangeStart}–${rangeEnd} of ${total}`;
  const hasPrevious = page > 1;
  const hasNext = page < totalPages;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: '28px',
        padding: '13px 22px',
        borderTop: `1px solid ${UI_TOKENS.divider}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Box component="span" sx={{ fontSize: '12.5px', color: UI_TOKENS.textSecondary }}>
          Rows per page:
        </Box>
        <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
          <Box
            component="select"
            value={rowsPerPage}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onRowsPerPageChange(Number(e.target.value))}
            sx={{
              appearance: 'none',
              WebkitAppearance: 'none',
              border: 'none',
              background: 'transparent',
              fontFamily: UI_TOKENS.fontSans,
              fontSize: 13,
              fontWeight: 500,
              color: UI_TOKENS.textStrong,
              padding: '2px 16px 2px 4px',
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            {rowsPerPageOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Box>
          <Box sx={{ position: 'absolute', right: 0, pointerEvents: 'none', display: 'flex' }}>
            <DropdownArrowDownIcon width={9} height={6} color={UI_TOKENS.textSecondary} />
          </Box>
        </Box>
      </Box>
      <Box component="span" sx={{ fontSize: '12.5px', color: UI_TOKENS.textSecondary, whiteSpace: 'nowrap' }}>
        {rangeLabel}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Box
          component="button"
          type="button"
          aria-label="Previous page"
          disabled={!hasPrevious}
          onClick={() => onPageChange(page - 1)}
          sx={{ ...pageButtonSx(!hasPrevious), '& svg': { transform: 'rotate(180deg)' } }}
        >
          <PhCaretRightSvg size={14} />
        </Box>
        <Box component="button" type="button" aria-label="Next page" disabled={!hasNext} onClick={() => onPageChange(page + 1)} sx={pageButtonSx(!hasNext)}>
          <PhCaretRightSvg size={14} />
        </Box>
      </Box>
    </Box>
  );
};

export default TableFooter;
